<template>
  <div v-if="visible && coupon" class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content modal-lg" @click.stop>
      <div class="modal-header">
        <h3>優惠券詳情</h3>
        <button class="modal-close" @click="$emit('close')">
          <i class="ri-close-line"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="detail-grid">
          <!-- 基本資訊 -->
          <div class="detail-section">
            <h4 class="section-subtitle">基本資訊</h4>
            <div class="detail-row">
              <span class="detail-label">優惠券名稱：</span>
              <span class="detail-value">{{ coupon.templateName || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">優惠券代碼：</span>
              <span class="detail-value code">{{ coupon.couponCode }}</span>
            </div>
            <div class="detail-row" v-if="coupon.couponType">
              <span class="detail-label">類型：</span>
              <span class="detail-value">{{ getCouponTypeText(coupon.couponType) }}</span>
            </div>
            <div class="detail-row" v-if="coupon.discountValue">
              <span class="detail-label">折扣：</span>
              <span class="detail-value highlight">{{ formatCouponValue(coupon.couponType, coupon.discountValue) }}</span>
            </div>
            <div class="detail-row" v-if="coupon.minPurchaseAmount">
              <span class="detail-label">最低消費：</span>
              <span class="detail-value">NT$ {{ coupon.minPurchaseAmount }}</span>
            </div>
            <div class="detail-row" v-if="coupon.maxDiscountAmount">
              <span class="detail-label">最高折抵：</span>
              <span class="detail-value">NT$ {{ coupon.maxDiscountAmount }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">狀態：</span>
              <span :class="getStatusBadgeClass(coupon.status)">
                {{ getStatusText(coupon.status) }}
              </span>
            </div>
          </div>

          <!-- 時間資訊 -->
          <div class="detail-section">
            <h4 class="section-subtitle">時間資訊</h4>
            <div class="detail-row">
              <span class="detail-label">發放時間：</span>
              <span class="detail-value">{{ formatDateTime(coupon.issuedAt) }}</span>
            </div>
            <div class="detail-row" v-if="coupon.validFrom">
              <span class="detail-label">生效日期：</span>
              <span class="detail-value">{{ formatDate(coupon.validFrom) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">有效期限：</span>
              <span class="detail-value">{{ coupon.validUntil ? formatDate(coupon.validUntil) : '無期限' }}</span>
            </div>
            <div class="detail-row" v-if="coupon.usedAt">
              <span class="detail-label">使用時間：</span>
              <span class="detail-value">{{ formatDateTime(coupon.usedAt) }}</span>
            </div>
            <div class="detail-row" v-if="coupon.cancelledAt">
              <span class="detail-label">{{ coupon.status === 'deleted' ? '刪除時間：' : '取消時間：' }}</span>
              <span class="detail-value">{{ formatDateTime(coupon.cancelledAt) }}</span>
            </div>
          </div>

          <!-- 發放資訊 -->
          <div class="detail-section">
            <h4 class="section-subtitle">發放資訊</h4>
            <div class="detail-row" v-if="coupon.issueSource">
              <span class="detail-label">來源平台：</span>
              <span class="detail-value">{{ getIssueSourceText(coupon.issueSource) }}</span>
            </div>
            <div class="detail-row" v-if="coupon.issueMethod">
              <span class="detail-label">發放方式：</span>
              <span class="detail-value">{{ getIssueMethodText(coupon.issueMethod) }}</span>
            </div>
            <div class="detail-row" v-if="coupon.issueReason">
              <span class="detail-label">發放備註：</span>
              <span class="detail-value">{{ coupon.issueReason }}</span>
            </div>
            <div class="detail-row" v-if="coupon.cancelReason">
              <span class="detail-label">{{ coupon.status === 'deleted' ? '刪除原因：' : '取消原因：' }}</span>
              <span class="detail-value cancel-text">{{ coupon.cancelReason }}</span>
            </div>
          </div>

          <!-- 使用資訊 -->
          <div class="detail-section" v-if="coupon.usedAt">
            <h4 class="section-subtitle">使用資訊</h4>
            <div class="detail-row" v-if="coupon.usedOrderId">
              <span class="detail-label">使用訂單：</span>
              <span class="detail-value">{{ coupon.usedOrderId }}</span>
            </div>
            <div class="detail-row" v-if="coupon.discountAmount">
              <span class="detail-label">實際折抵：</span>
              <span class="detail-value">NT$ {{ coupon.discountAmount }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="$emit('close')">關閉</button>
        <button
          v-if="coupon.status === 'unused'"
          class="btn btn-danger"
          @click="$emit('cancel', coupon)"
        >
          <i class="ri-close-circle-line"></i>
          取消優惠券
        </button>
        <button
          v-if="coupon.status !== 'used' && coupon.status !== 'cancelled' && coupon.status !== 'deleted'"
          class="btn btn-outline-danger"
          @click="$emit('delete', coupon)"
        >
          <i class="ri-delete-bin-line"></i>
          刪除票券
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CouponDetailModal',
  props: {
    visible: { type: Boolean, default: false },
    coupon: { type: Object, default: null }
  },
  emits: ['close', 'cancel', 'delete'],
  methods: {
    getStatusText(status) {
      const map = {
        unused: '未使用',
        transferring: '轉送中',
        transferred_out: '已轉出',
        used: '已使用',
        expired: '已過期',
        cancelled: '已取消',
        deleted: '已刪除',
        transferred: '已轉讓'
      };
      return map[status] || status;
    },
    getStatusBadgeClass(status) {
      const map = {
        unused: 'badge badge-success',
        transferring: 'badge badge-warning',
        transferred_out: 'badge badge-info',
        used: 'badge badge-primary',
        expired: 'badge badge-warning',
        cancelled: 'badge badge-secondary',
        deleted: 'badge badge-danger',
        transferred: 'badge badge-info'
      };
      return map[status] || 'badge badge-secondary';
    },
    getCouponTypeText(type) {
      const map = {
        discount: '折扣券',
        fixed_amount: '折價券',
        free_shipping: '免運券',
        gift: '贈品券'
      };
      return map[type] || type;
    },
    formatCouponValue(type, value) {
      if (!value) return '-';
      if (type === 'discount') return `${value}% 折扣`;
      if (type === 'fixed_amount') return `NT$ ${value} 折抵`;
      if (type === 'free_shipping') return '免運費';
      return `${value}`;
    },
    getIssueSourceText(source) {
      const map = {
        crm_admin: 'CRM 管理後台',
        pos_system: 'POS 系統',
        mobile_app: '手機 APP',
        external_api: '外部 API',
        system: '系統自動'
      };
      return map[source] || source || '-';
    },
    getIssueMethodText(method) {
      const map = {
        manual: '手動發放',
        batch: '批次發放',
        batch_operation: '批次發放',
        order: '訂單贈送',
        activity: '活動發放',
        promotion: '促銷活動',
        sign_in: '簽到獎勵',
        referral: '推薦獎勵',
        birthday: '生日禮',
        register: '註冊贈送',
        system: '系統發放'
      };
      return map[method] || method || '-';
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('zh-TW', {
        year: 'numeric', month: '2-digit', day: '2-digit'
      });
    },
    formatDateTime(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('zh-TW', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
}

.modal-lg {
  max-width: 680px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 0 4px;
  line-height: 1;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

@media (max-width: 600px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}

.detail-section {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.section-subtitle {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin: 0 0 8px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid #eee;
}

.detail-row {
  display: flex;
  gap: 6px;
  padding: 5px 0;
  font-size: 14px;
  align-items: flex-start;
}

.detail-label {
  color: #888;
  white-space: nowrap;
  min-width: 80px;
  flex-shrink: 0;
}

.detail-value {
  color: #333;
  word-break: break-all;
}

.detail-value.code {
  font-family: monospace;
  font-size: 13px;
  background: #f5f5f5;
  padding: 1px 6px;
  border-radius: 3px;
}

.detail-value.highlight {
  color: #e67e22;
  font-weight: 600;
}

.cancel-text {
  color: #dc3545;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s;
}

.btn-secondary {
  background: #6c757d;
  color: #fff;
  border-color: #6c757d;
}

.btn-secondary:hover { background: #5a6268; }

.btn-danger {
  background: #dc3545;
  color: #fff;
  border-color: #dc3545;
}

.btn-danger:hover { background: #c82333; }

.btn-outline-danger {
  background: transparent;
  color: #dc3545;
  border-color: #dc3545;
}

.btn-outline-danger:hover {
  background: #dc3545;
  color: #fff;
}

/* Badges */
.badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.badge-success { background: #d4edda; color: #155724; }
.badge-primary { background: #cce5ff; color: #004085; }
.badge-warning { background: #fff3cd; color: #856404; }
.badge-secondary { background: #e2e3e5; color: #383d41; }
.badge-danger { background: #f8d7da; color: #721c24; }
.badge-info { background: #d1ecf1; color: #0c5460; }
</style>
